
import React from 'react';
import { Activity, Zap, CheckCircle2, ShieldCheck, Target, BarChart3, Lock, ChevronRight, Globe, Shield, ArrowRight } from 'lucide-react';

interface LandingPageProps {
  onEnter: () => void;
}

const LandingPage: React.FC<LandingPageProps> = ({ onEnter }) => {
  const currentYear = new Date().getFullYear();

  return (
    <div className="min-h-screen bg-[#0b0e11] text-[#eaecef] font-sans selection:bg-[#00ff66] selection:text-black">
      {/* Background Glows */}
      <div className="fixed top-[-10%] left-[-10%] w-[60%] h-[60%] bg-[#00ff66]/5 rounded-full blur-[150px] -z-10"></div>

      {/* Fixed Header */}
      <nav className="fixed top-0 left-0 w-full h-20 bg-[#0b0e11]/90 backdrop-blur-xl border-b border-[#474d57]/20 z-[100] px-6 md:px-12 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="bg-[#00ff66] p-1.5 rounded-lg shadow-[0_0_20px_rgba(0,255,102,0.5)]">
            <Activity className="text-black" size={22} strokeWidth={3} />
          </div>
          <span className="text-xl font-black tracking-tighter uppercase text-white">FIFA<span className="text-[#00ff66]">LYTICS</span></span>
        </div>

        <div className="flex items-center gap-6 md:gap-10">
          <button
            onClick={onEnter}
            className="hidden md:block text-[10px] font-black uppercase tracking-[0.2em] text-[#eaecef] hover:text-[#00ff66] transition-colors"
          >
            ACESSAR PAINEL
          </button>
          <button
            onClick={onEnter}
            className="bg-[#00ff66] text-black px-6 py-2.5 rounded-full text-[10px] font-black uppercase tracking-[0.15em] hover:scale-105 active:scale-95 transition-all shadow-[0_0_25px_rgba(0,255,102,0.4)] flex items-center gap-2"
          >
            COMEÇAR AGORA <ArrowRight size={14} strokeWidth={3} />
          </button>
        </div>
      </nav>

      {/* Hero Content Wrapper */}
      <div className="pt-24">
        {/* Header Badge */}
        <div className="pt-12 flex justify-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#00ff66]/30 bg-[#00ff66]/5 backdrop-blur-sm">
            <ShieldCheck size={14} className="text-[#00ff66]" />
            <span className="text-[9px] font-black uppercase tracking-[0.2em] text-[#00ff66]">Idealizado pelo Dupla Fifa Tips</span>
          </div>
        </div>

        {/* Hero Section */}
        <header className="pt-16 pb-12 px-6 max-w-5xl mx-auto text-center">
          <h1 className="text-6xl md:text-8xl font-black uppercase italic leading-[0.9] tracking-tighter mb-8 animate-in fade-in slide-in-from-bottom-8 duration-1000">
            DOMINE O <span className="text-[#00ff66]">LUCRO</span><br />
            NO FIFA ONLINE.
          </h1>

          <p className="text-sm md:text-base text-[#848e9c] max-w-xl mx-auto font-bold leading-relaxed mb-12">
            Chega de operar no escuro. A ferramenta definitiva de análise e<br className="hidden md:block" />
            controle de banca, validada pelo <span className="text-white border-b-2 border-[#00ff66]">Dupla Fifa</span> para traders que buscam<br className="hidden md:block" />
            consistência real.
          </p>

          <button
            onClick={onEnter}
            className="bg-[#00ff66] text-[#0b0e11] px-12 py-5 rounded-2xl font-black uppercase tracking-widest text-sm hover:shadow-[0_0_40px_rgba(0,255,102,0.5)] hover:scale-105 active:scale-95 transition-all flex items-center gap-3 mx-auto"
          >
            QUERO O ACESSO AGORA <Zap size={18} fill="currentColor" />
          </button>
        </header>

        {/* Persuasive Section */}
        <section className="pb-24 px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-4 bg-[#1e2329] border border-[#474d57]/30 p-6 rounded-[32px] mb-8">
              <div className="flex flex-col items-center">
                <span className="text-[10px] font-black text-[#848e9c] uppercase tracking-widest mb-1">Assertividade</span>
                <div className="flex items-center gap-2">
                  <ShieldCheck className="text-[#00ff66]" size={18} />
                  <span className="text-2xl font-black text-[#00ff66] italic">75.0% VERIFICADA</span>
                </div>
              </div>
            </div>

            <div className="flex flex-col items-center gap-8 mt-12">
              <p className="text-[9px] font-black uppercase tracking-[0.4em] text-[#474d57]">
                OTIMIZADO PARA OPERAÇÕES NAS MAIORES CASAS DO MUNDO
              </p>
              <div className="flex flex-wrap justify-center items-center gap-10 md:gap-20 transition-all duration-500">
                <img src="https://logodownload.org/wp-content/uploads/2019/03/bet365-logo-.png" alt="Bet365" className="h-6 md:h-8 object-contain" />
                <img src="https://upload.wikimedia.org/wikipedia/commons/0/0f/Betano-logo-5.png" alt="Betano" className="h-8 md:h-10 object-contain" />
                <img src="https://logodownload.org/wp-content/uploads/2019/05/sportingbet-logo.png" alt="Sportingbet" className="h-6 md:h-8 object-contain" />
              </div>
            </div>
          </div>
        </section>

        {/* Tools Ecosystem Section */}
        <section className="py-24 px-6 max-w-6xl mx-auto border-t border-[#474d57]/20">
          <div className="text-center mb-16">
            <span className="text-[10px] font-black uppercase tracking-[0.4em] text-[#00ff66]">The Next Level</span>
            <h2 className="text-4xl md:text-5xl font-black uppercase italic tracking-tighter mt-4">
              O ECOSSISTEMA DE<br />FERRAMENTAS PRO
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-[#1e2329] border-2 border-[#00ff66] rounded-[40px] p-8 relative shadow-[0_0_40px_rgba(0,255,102,0.1)]">
              <div className="flex items-center justify-between mb-8">
                <div className="bg-[#00ff66] p-3 rounded-2xl shadow-[0_0_20px_rgba(0,255,102,0.2)]">
                  <Activity size={24} className="text-[#0b0e11]" strokeWidth={3} />
                </div>
                <span className="bg-[#00ff66] text-[#0b0e11] text-[8px] font-black px-3 py-1 rounded-full uppercase tracking-widest">Ativo Agora</span>
              </div>
              <h3 className="text-2xl font-black uppercase italic mb-4 text-white">FIFA<span className="text-[#00ff66]">LYTICS</span></h3>
              <p className="text-xs text-[#848e9c] font-bold leading-relaxed mb-10">
                O cérebro da sua operação. Controle de banca, monitoramento de ROI e análise de performance de players em uma interface de alta velocidade.
              </p>
              <div className="pt-6 border-t border-[#474d57]/30 flex items-center justify-between">
                <span className="text-[9px] font-black uppercase tracking-widest text-[#00ff66]">⚡ Já disponível para membros</span>
              </div>
            </div>

            <div className="bg-[#1e2329]/40 border border-[#474d57]/30 rounded-[40px] p-8 opacity-80 hover:opacity-100 hover:border-[#00ff66]/50 hover:shadow-[0_0_30px_rgba(0,255,102,0.1)] transition-all group">
              <div className="flex items-center justify-between mb-8">
                <div className="bg-[#2b3139] p-3 rounded-2xl group-hover:bg-[#00ff66]/10 transition-colors">
                  <Globe size={24} className="text-[#848e9c] group-hover:text-[#00ff66] transition-colors" />
                </div>
                <span className="bg-[#2b3139] text-[#848e9c] text-[8px] font-black px-3 py-1 rounded-full uppercase tracking-widest group-hover:bg-[#00ff66] group-hover:text-[#0b0e11] transition-all">Em Breve</span>
              </div>
              <h3 className="text-2xl font-black uppercase italic mb-4 text-white">ROOM<span className="text-[#00ff66]">TIPS</span></h3>
              <p className="text-xs text-[#848e9c] font-bold leading-relaxed mb-10">
                Monitoramento inteligente de lobbies. Identifique salas lucrativas e padrões de over/under em tempo real antes de todo o mercado.
              </p>
              <div className="pt-6 border-t border-[#474d57]/30 flex items-center justify-between">
                <span className="text-[9px] font-black uppercase tracking-widest text-[#848e9c] flex items-center gap-1.5 group-hover:text-[#00ff66] transition-colors">
                  <Lock size={10} /> Acesso Restrito
                </span>
              </div>
            </div>

            <div className="bg-[#1e2329]/40 border border-[#474d57]/30 rounded-[40px] p-8 opacity-80 hover:opacity-100 hover:border-[#00ff66]/50 hover:shadow-[0_0_30px_rgba(0,255,102,0.1)] transition-all group">
              <div className="flex items-center justify-between mb-8">
                <div className="bg-[#2b3139] p-3 rounded-2xl group-hover:bg-[#00ff66]/10 transition-colors">
                  <Target size={24} className="text-[#848e9c] group-hover:text-[#00ff66] transition-colors" />
                </div>
                <span className="bg-[#2b3139] text-[#848e9c] text-[8px] font-black px-3 py-1 rounded-full uppercase tracking-widest group-hover:bg-[#00ff66] group-hover:text-[#0b0e11] transition-all">Em Breve</span>
              </div>
              <h3 className="text-2xl font-black uppercase italic mb-4 text-white">TROOPER<span className="text-[#00ff66]">TIPS</span></h3>
              <p className="text-xs text-[#848e9c] font-bold leading-relaxed mb-10">
                Estratégias avançadas de "Sniper". Filtros live personalizados e acompanhamento tático para entradas de alta precisão baseadas em algoritmos proprietários.
              </p>
              <div className="pt-6 border-t border-[#474d57]/30 flex items-center justify-between">
                <span className="text-[9px] font-black uppercase tracking-widest text-[#848e9c] flex items-center gap-1.5 group-hover:text-[#00ff66] transition-colors">
                  <Lock size={10} /> Fase de testes
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Special Session Section - As seen in prompt screenshot */}
        <section className="py-24 bg-[#0b0e11] px-6 text-center border-t border-[#474d57]/20">
          <span className="text-[10px] font-black text-[#00ff66] uppercase tracking-[0.4em]">Sessões Especiais</span>
          <h2 className="text-5xl md:text-7xl font-black uppercase italic tracking-tighter mt-4 leading-none">
            CORUJÕES <span className="text-[#00ff66]">DIFERENCIADOS</span>
          </h2>
          <p className="text-sm text-[#848e9c] font-bold mt-6 max-w-2xl mx-auto leading-relaxed">
            Acompanhe as sessões live onde aplicamos a inteligência do FifaLytics para alavancar bancas em tempo real com transparência total.
          </p>

          <div className="mt-10">
            <a
              href="https://www.youtube.com/channel/UCpKwSBQWkUaE9XIQUyU0gzw"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-[#ff0000] text-white px-8 py-4 rounded-2xl font-black uppercase tracking-widest text-xs hover:shadow-[0_0_30px_rgba(255,0,0,0.3)] hover:scale-105 active:scale-95 transition-all"
            >
              <svg
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="currentColor"
              >
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
              </svg>
              Acessar Canal YouTube
            </a>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-24 px-6 text-center max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-6xl font-black uppercase italic tracking-tighter leading-[0.9] mb-12">
            O PRÓXIMO NÍVEL<br />COMEÇA <span className="text-[#00ff66]">AGORA.</span>
          </h2>

          <div className="bg-[#1e2329] border-2 border-[#00ff66]/50 rounded-[48px] p-8 md:p-16 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1 bg-[#00ff66]/30"></div>

            <div className="space-y-2 mb-8">
              <span className="text-[10px] font-black uppercase tracking-[0.4em] text-[#848e9c]">Garanta sua Vaga</span>
              <div className="flex items-center justify-center gap-4">
                <span className="text-4xl font-black text-white italic">PLANOS <span className="text-[#00ff66]">DISPONÍVEIS</span></span>
              </div>
              <p className="text-[10px] font-black uppercase tracking-widest text-[#00ff66]">Acesso Imediato ao Ecossistema Pro</p>
            </div>

            <button
              onClick={onEnter}
              className="w-full bg-[#00ff66] text-[#0b0e11] py-6 rounded-2xl font-black uppercase tracking-widest text-base hover:shadow-[0_0_50px_rgba(0,255,102,0.4)] transition-all flex items-center justify-center gap-3"
            >
              LIBERAR MEU ACESSO AGORA <Zap size={20} fill="currentColor" />
            </button>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-20 border-t border-[#474d57]/30 text-center bg-[#0b0e11]">
          <div className="flex flex-col items-center">
            <div className="flex items-center gap-3 mb-8">
              <div className="bg-[#00ff66] p-1.5 rounded-lg shadow-[0_0_20px_rgba(0,255,102,0.5)]">
                <Activity className="text-black" size={22} strokeWidth={3} />
              </div>
              <span className="text-xl font-black tracking-tighter uppercase text-white">FIFA<span className="text-[#00ff66]">LYTICS</span></span>
            </div>

            <p className="text-[#00ff66] text-[10px] font-black uppercase tracking-[0.4em] mb-4">
              © {currentYear} FIFALYTICS PRO • IDEALIZADO PELO DUPLA FIFA SOLUTIONS
            </p>

            <p className="text-[#848e9c] text-[8px] font-bold uppercase tracking-[0.15em] max-w-2xl mx-auto opacity-70 leading-relaxed">
              APOSTAS ESPORTIVAS ENVOLVEM RISCO NO FIFA ONLINE. OPERE COM RESPONSABILIDADE. ESTA FERRAMENTA<br className="hidden md:block" />
              É PARA FINS ANALÍTICOS E NÃO GARANTE LUCROS FINANCEIROS.
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default LandingPage;
